import { jsPDF } from "jspdf";
import type {
  BlockDocumentContent,
  SectionBlock,
  TextBlock,
  TableBlock,
  DyadMatrixBlock,
} from "@unruly-global/api-types";
import type { LocationCluster } from "./resolveLocation";
import {
  KEY_EVENTS,
  KEY_EVENTS_MONTHS,
  KEY_EVENTS_START,
  KEY_EVENTS_END,
} from "../components/KeyEventsChart/keyEventsData";

// ---------------------------------------------------------------------------
// Colors (matching bentham palette)
// ---------------------------------------------------------------------------

const C = {
  dark: [26, 35, 50] as const,
  orange: [227, 83, 2] as const,
  accent: [0, 104, 183] as const,
  text: [26, 26, 26] as const,
  muted: [85, 85, 85] as const,
  lightBg: [245, 245, 245] as const,
  white: [255, 255, 255] as const,
  green: [10, 102, 64] as const,
};

const DYAD_COLORS: Record<
  number,
  {
    bg: [number, number, number];
    text: [number, number, number];
    label: string;
  }
> = {
  1: { bg: [217, 64, 82], text: [255, 255, 255], label: "Hostile" },
  2: { bg: [232, 168, 56], text: [26, 26, 26], label: "Tense" },
  3: { bg: [229, 198, 66], text: [26, 26, 26], label: "Stable" },
  4: { bg: [62, 170, 109], text: [255, 255, 255], label: "Friendly" },
  5: { bg: [29, 122, 78], text: [255, 255, 255], label: "Close" },
};

// ---------------------------------------------------------------------------
// Page layout constants
// ---------------------------------------------------------------------------

const PM = 12; // portrait margin
const P_CW = 210 - PM * 2; // portrait content width
const P_CH = 297 - PM * 2; // portrait content height
const BODY_FS = 9;
const BODY_LH = 4;
const SECTION_FS = 9.5;
const TITLE_FS = 8;

// ---------------------------------------------------------------------------
// Static map projection — matches world-map-bg.jpg bounds
// ---------------------------------------------------------------------------

const MAP_LAT_TOP = 79.56;
const MAP_LAT_BOTTOM = -70.38;
const MAP_LNG_LEFT = -180;
const MAP_LNG_RIGHT = 180;

function latToMercY(lat: number): number {
  const r = (lat * Math.PI) / 180;
  return Math.log(Math.tan(Math.PI / 4 + r / 2));
}

const MERC_TOP = latToMercY(MAP_LAT_TOP);
const MERC_BOTTOM = latToMercY(MAP_LAT_BOTTOM);

function projectToMap(
  lat: number,
  lng: number,
  x0: number,
  y0: number,
  w: number,
  h: number,
): [number, number] {
  const px = x0 + ((lng - MAP_LNG_LEFT) / (MAP_LNG_RIGHT - MAP_LNG_LEFT)) * w;
  const mercY = latToMercY(lat);
  const py = y0 + ((MERC_TOP - mercY) / (MERC_TOP - MERC_BOTTOM)) * h;
  return [px, py];
}

// ---------------------------------------------------------------------------
// Location label layout — places region labels with blurbs around map dots
// ---------------------------------------------------------------------------

interface PlacedLabel {
  x: number;
  y: number;
  w: number;
  h: number;
  region: string;
  blurbs: string[];
}

interface ProjectedCluster {
  cluster: LocationCluster;
  px: number;
  py: number;
}

const LABEL_W = 36; // mm
const LABEL_PAD = 1.2;
const LABEL_TITLE_FS = 5.5;
const LABEL_BODY_FS = 4.5;
const LABEL_LINE_H = 2;
const LABEL_TITLE_H = 2.8;
const MAX_BLURBS_PER_LABEL = 3;
const MAX_LINES_PER_BLURB = 2;
const DOT_BUFFER = 1.8; // avoid covering other cluster dots

function measureLabel(pdf: jsPDF, blurbs: string[]): { w: number; h: number } {
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(LABEL_BODY_FS);
  const contentW = LABEL_W - LABEL_PAD * 2;
  let bodyLines = 0;
  for (const b of blurbs) {
    const lines = pdf
      .splitTextToSize(`\u2022 ${b}`, contentW)
      .slice(0, MAX_LINES_PER_BLURB);
    bodyLines += lines.length;
  }
  const h =
    LABEL_PAD + LABEL_TITLE_H + 1.2 + bodyLines * LABEL_LINE_H + LABEL_PAD;
  return { w: LABEL_W, h };
}

function rectsOverlap(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number },
  pad = 0.5,
): boolean {
  return !(
    a.x + a.w + pad <= b.x ||
    b.x + b.w + pad <= a.x ||
    a.y + a.h + pad <= b.y ||
    b.y + b.h + pad <= a.y
  );
}

function pointInRect(
  px: number,
  py: number,
  r: { x: number; y: number; w: number; h: number },
  pad: number,
): boolean {
  return (
    px >= r.x - pad &&
    px <= r.x + r.w + pad &&
    py >= r.y - pad &&
    py <= r.y + r.h + pad
  );
}

/** Greedy placement — tries 8 directions × 2 radii around each dot. */
function layoutLocationLabels(
  pdf: jsPDF,
  projected: ProjectedCluster[],
  mapLeft: number,
  mapTop: number,
  mapW: number,
  _mapH: number,
  pageBottomExcl: number,
): Array<ProjectedCluster & { label: PlacedLabel | null }> {
  const pageLeft = mapLeft;
  const pageRight = mapLeft + mapW;
  const pageTop = mapTop;
  const pageBottom = pageBottomExcl;

  // Sort by northward latitude (higher first) so tops get prime real estate
  const sorted = [...projected].sort((a, b) => a.py - b.py);

  const placed: PlacedLabel[] = [];
  const results: Array<ProjectedCluster & { label: PlacedLabel | null }> = [];

  // 8 directions × 2 radii = 16 candidate offsets
  const dirs: Array<[number, number]> = [
    [1, 0], // E
    [-1, 0], // W
    [0, -1], // N
    [0, 1], // S
    [1, -1], // NE
    [-1, -1], // NW
    [1, 1], // SE
    [-1, 1], // SW
  ];
  const radii = [5, 12, 20];

  for (const p of sorted) {
    const region = p.cluster.label;
    const blurbs = p.cluster.blockBlurbs
      .filter(Boolean)
      .slice(0, MAX_BLURBS_PER_LABEL);
    if (blurbs.length === 0) {
      results.push({ ...p, label: null });
      continue;
    }

    const { w, h } = measureLabel(pdf, blurbs);

    let best: PlacedLabel | null = null;
    outer: for (const r of radii) {
      for (const [dx, dy] of dirs) {
        // Anchor label so its nearest edge sits at dot ± (dx*r, dy*r)
        let lx = p.px + dx * r;
        let ly = p.py + dy * r;
        // Align so the box is on the requested side (not centered through dot)
        if (dx < 0) lx -= w;
        else if (dx === 0) lx -= w / 2;
        if (dy < 0) ly -= h;
        else if (dy === 0) ly -= h / 2;

        // Keep fully inside page area
        if (
          lx < pageLeft ||
          lx + w > pageRight ||
          ly < pageTop ||
          ly + h > pageBottom
        )
          continue;

        const candidate = { x: lx, y: ly, w, h, region, blurbs };

        // Don't overlap other labels
        let ok = true;
        for (const other of placed) {
          if (rectsOverlap(candidate, other)) {
            ok = false;
            break;
          }
        }
        if (!ok) continue;

        // Don't cover another cluster's dot
        for (const other of projected) {
          if (other === p) continue;
          if (pointInRect(other.px, other.py, candidate, DOT_BUFFER)) {
            ok = false;
            break;
          }
        }
        if (!ok) continue;

        best = candidate;
        break outer;
      }
    }

    if (best) placed.push(best);
    results.push({ ...p, label: best });
  }

  return results;
}

function renderLocationLabel(pdf: jsPDF, label: PlacedLabel): void {
  const { x, y, w, h, region, blurbs } = label;
  const blurbLines: string[][] = [];
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(LABEL_BODY_FS);
  for (const b of blurbs) {
    blurbLines.push(
      pdf
        .splitTextToSize(`\u2022 ${b}`, w - LABEL_PAD * 2)
        .slice(0, MAX_LINES_PER_BLURB),
    );
  }

  // Background
  pdf.setFillColor(255, 255, 255);
  pdf.setDrawColor(180, 180, 180);
  pdf.setLineWidth(0.2);
  pdf.roundedRect(x, y, w, h, 0.8, 0.8, "FD");

  // Region title
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(LABEL_TITLE_FS);
  pdf.setTextColor(...C.dark);
  pdf.text(region, x + LABEL_PAD, y + LABEL_PAD + LABEL_TITLE_FS * 0.35);

  // Blurbs
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(LABEL_BODY_FS);
  pdf.setTextColor(...C.text);
  let ty = y + LABEL_PAD + LABEL_TITLE_H + 1.2;
  for (const lines of blurbLines) {
    for (const line of lines) {
      pdf.text(line, x + LABEL_PAD, ty);
      ty += LABEL_LINE_H;
    }
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/\[(.*?)\]\(.*?\)/g, "$1")
    .replace(/^[-*+]\s+/gm, "\u2022 ")
    .replace(/^#{1,6}\s+/gm, "");
}

/** Parse markdown text into segments preserving bold spans. */
interface TextRun {
  text: string;
  bold: boolean;
}

function parseMarkdownRuns(text: string): TextRun[] {
  const runs: TextRun[] = [];
  // Strip links and headings first
  const cleaned = text
    .replace(/\[(.*?)\]\(.*?\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "");
  const re = /\*\*(.*?)\*\*/g;
  let lastIdx = 0;
  let match;
  while ((match = re.exec(cleaned)) !== null) {
    if (match.index > lastIdx) {
      runs.push({ text: cleaned.slice(lastIdx, match.index), bold: false });
    }
    runs.push({ text: match[1], bold: true });
    lastIdx = re.lastIndex;
  }
  if (lastIdx < cleaned.length) {
    runs.push({ text: cleaned.slice(lastIdx), bold: false });
  }
  return runs;
}

/** Render text with inline bold spans, wrapping across lines. */
function renderRichText(
  pdf: jsPDF,
  runs: TextRun[],
  x: number,
  maxW: number,
  startY: number,
  lineH: number,
  fontSize: number,
  pageBottom: number,
): number {
  let curX = x;
  let y = startY;

  for (const run of runs) {
    pdf.setFont("helvetica", run.bold ? "bold" : "normal");
    pdf.setFontSize(fontSize);
    pdf.setTextColor(...C.text);

    const words = run.text.split(/(\s+)/);
    for (const word of words) {
      if (!word) continue;
      const ww = pdf.getTextWidth(word);
      if (curX + ww > x + maxW && curX > x) {
        curX = x;
        y += lineH;
        if (y > pageBottom) {
          pdf.addPage("a4", "portrait");
          y = PM;
        }
      }
      pdf.text(word, curX, y);
      curX += ww;
    }
  }
  y += lineH;
  return y;
}

// ---------------------------------------------------------------------------
// Main export function
// ---------------------------------------------------------------------------

export interface ExportPdfOptions {
  title: string;
  subtitle: string;
  document: BlockDocumentContent;
  clusters: LocationCluster[];
}

// Load static map image once and cache
let mapImageCache: string | null = null;
async function loadMapImage(): Promise<string> {
  if (mapImageCache) return mapImageCache;
  const res = await fetch("/world-map-bg.jpg");
  const blob = await res.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      mapImageCache = reader.result as string;
      resolve(mapImageCache);
    };
    reader.readAsDataURL(blob);
  });
}

export async function exportCollectionPdf(
  options: ExportPdfOptions,
): Promise<void> {
  const { title, subtitle, document: doc } = options;

  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const lW = pdf.internal.pageSize.getWidth();
  const lH = pdf.internal.pageSize.getHeight();
  const mg = 10;

  // ── Page 1: Landscape — Title + Map ──

  // Title
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(16);
  pdf.setTextColor(...C.dark);
  pdf.text(title, mg, 14);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(11);
  pdf.setTextColor(...C.accent);
  pdf.text(subtitle, lW - mg, 14, { align: "right" });

  // Find dyad matrix block to render on page 1 below the map
  const dyadBlock = doc.blocks.find(
    (b): b is DyadMatrixBlock => b.type === "dyad_matrix",
  );

  // Calculate dyad table height so we know how much space the map gets
  // title(7) + header(8) + status(6) + headline(12) + padding(4) = ~37
  const dyadTableH = dyadBlock && dyadBlock.dyads.length > 0 ? 37 : 0;
  const footerH = 10;
  const mapTop = 20;
  const availableH = lH - mapTop - dyadTableH - footerH;

  // Static map background + projected dots
  const mapDrawW = lW - mg * 2;
  const mapLeft = mg;
  // Static image is 1024x676 (aspect ~1.515)
  const mapAspect = 1024 / 676;
  const mapDrawH = Math.min(mapDrawW / mapAspect, availableH);

  try {
    const mapDataUrl = await loadMapImage();
    pdf.addImage(mapDataUrl, "JPEG", mapLeft, mapTop, mapDrawW, mapDrawH);
  } catch {
    pdf.setFillColor(240, 240, 240);
    pdf.rect(mapLeft, mapTop, mapDrawW, mapDrawH, "F");
  }

  // Draw dots at Mercator-projected positions + labels with blurbs
  type PlacedEntry = ProjectedCluster & { label: PlacedLabel | null };
  let placedLabels: PlacedEntry[] = [];
  if (options.clusters.length > 0) {
    const DOT_R = 1.0;

    // Project all clusters to screen coords first (skip off-map ones)
    const projected: Array<{
      cluster: LocationCluster;
      px: number;
      py: number;
    }> = [];
    for (const cluster of options.clusters) {
      const [lat, lng] = cluster.coords;
      const [px, py] = projectToMap(
        lat,
        lng,
        mapLeft,
        mapTop,
        mapDrawW,
        mapDrawH,
      );
      if (
        px < mapLeft ||
        px > mapLeft + mapDrawW ||
        py < mapTop ||
        py > mapTop + mapDrawH
      )
        continue;
      projected.push({ cluster, px, py });
    }

    // Compute layout for labels (skip placement if no blurbs)
    const mapBottomExcl = lH - dyadTableH - footerH;
    placedLabels = layoutLocationLabels(
      pdf,
      projected,
      mapLeft,
      mapTop,
      mapDrawW,
      mapDrawH,
      mapBottomExcl,
    );

    // Draw leader lines first (so dots + labels render on top)
    pdf.setDrawColor(80, 80, 80);
    pdf.setLineWidth(0.15);
    for (const p of placedLabels) {
      if (!p.label) continue;
      const { label, px, py } = p;
      // Nearest edge midpoint of label box to dot
      const cx = label.x + label.w / 2;
      const cy = label.y + label.h / 2;
      let lx = cx;
      let ly = cy;
      if (px < label.x) lx = label.x;
      else if (px > label.x + label.w) lx = label.x + label.w;
      if (py < label.y) ly = label.y;
      else if (py > label.y + label.h) ly = label.y + label.h;
      pdf.line(lx, ly, px, py);
    }

    // Draw dots
    for (const p of projected) {
      pdf.setFillColor(76, 144, 240);
      pdf.circle(p.px, p.py, DOT_R, "F");
      pdf.setDrawColor(255, 255, 255);
      pdf.setLineWidth(0.3);
      pdf.circle(p.px, p.py, DOT_R, "S");
    }

    // Draw label boxes
    for (const p of placedLabels) {
      if (!p.label) continue;
      renderLocationLabel(pdf, p.label);
    }
  }

  // Dyad summary table pinned to bottom of page
  let dyadCellRects: DyadCellRect[] = [];
  if (dyadBlock && dyadBlock.dyads.length > 0) {
    const dyadY = lH - dyadTableH - footerH + 2;
    dyadCellRects = renderDyadMatrixOnPage1(pdf, dyadBlock, mg, dyadY, lW, lH);
  }

  addFooter(pdf, title, subtitle);

  // ── Report pages: native selectable text ──

  // Start first content page
  pdf.addPage("a4", "portrait");
  let curY = PM;

  // Track which page each block lands on (uid → page number)
  const blockPageMap = new Map<string, number>();

  function newPage(orientation: "portrait" | "landscape" = "portrait") {
    pdf.addPage("a4", orientation);
    curY = PM;
  }

  function ensureSpace(needed: number) {
    if (curY + needed > PM + P_CH) newPage();
  }

  // Group blocks into sections for rendering
  const blocks = doc.blocks.filter((b) => b.type !== "toc");

  for (const block of blocks) {
    // Record current page for this block before rendering
    if (block.uid) {
      blockPageMap.set(block.uid, pdf.getNumberOfPages());
    }

    switch (block.type) {
      case "section":
        renderSection(
          pdf,
          block,
          ensureSpace,
          () => curY,
          (y) => {
            curY = y;
          },
        );
        break;
      case "text":
        renderTextBlock(
          pdf,
          block,
          ensureSpace,
          () => curY,
          (y) => {
            curY = y;
          },
        );
        break;
      case "table":
        renderTableBlock(
          pdf,
          block,
          ensureSpace,
          () => curY,
          (y) => {
            curY = y;
          },
        );
        break;
      case "dyad_matrix":
        // Rendered on page 1 below map — skip here
        break;
    }

    // Update page after rendering (ensureSpace may have added a page)
    if (block.uid) {
      blockPageMap.set(block.uid, pdf.getNumberOfPages());
    }
  }

  // ── Key Events — native PDF Gantt chart ──
  renderKeyEventsPdf(pdf, newPage);

  // ── Add internal links on page 1 callout labels → block pages ──
  if (placedLabels.length > 0) {
    pdf.setPage(1);
    for (const entry of placedLabels) {
      if (!entry.label) continue;
      const cluster = entry.cluster;

      // Find the first titleUid that has a page recorded
      let targetPage: number | undefined;
      for (const uid of cluster.titleUids) {
        const p = blockPageMap.get(uid);
        if (p) {
          targetPage = p;
          break;
        }
      }
      // Also check blockUids as fallback
      if (!targetPage) {
        for (const uid of cluster.blockUids) {
          const p = blockPageMap.get(uid);
          if (p) {
            targetPage = p;
            break;
          }
        }
      }
      if (!targetPage) continue;

      const { x, y, w, h } = entry.label;
      pdf.link(x, y, w, h, { pageNumber: targetPage });
    }
  }

  // ── Add internal links on page 1 dyad headline cells → dyad text blocks ──
  if (dyadCellRects.length > 0) {
    // Build dyadId → page number from text blocks
    const dyadIdToPage = new Map<string, number>();
    for (const block of blocks) {
      if (
        block.type === "text" &&
        block.dyadId &&
        blockPageMap.has(block.uid)
      ) {
        dyadIdToPage.set(block.dyadId, blockPageMap.get(block.uid)!);
      }
    }

    pdf.setPage(1);
    for (const cell of dyadCellRects) {
      if (!cell.dyadId) continue;
      const targetPage = dyadIdToPage.get(cell.dyadId);
      if (!targetPage) continue;
      pdf.link(cell.x, cell.y, cell.w, cell.h, {
        pageNumber: targetPage,
      });
    }
  }

  // ── Add footers to all pages ──
  const totalPages = pdf.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    addFooter(pdf, title, subtitle, i, totalPages);
  }

  // ── Save ──
  const monthSlug = subtitle.replace(/\s+/g, "_");
  pdf.save(`Unruly_Geopolitical_Overview_${monthSlug}.pdf`);
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

function addFooter(
  pdf: jsPDF,
  _title: string,
  subtitle: string,
  pageNum?: number,
  totalPages?: number,
) {
  const w = pdf.internal.pageSize.getWidth();
  const h = pdf.internal.pageSize.getHeight();
  const y = h - 6;

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(...C.muted);
  pdf.text(`Unruly Intelligence \u2014 ${subtitle}`, 10, y);

  // "Back to map" link in center (pages 2+)
  if (pageNum && pageNum > 1) {
    pdf.setTextColor(...C.accent);
    const linkText = "Back to map";
    const linkW = pdf.getTextWidth(linkText);
    const linkX = w / 2 - linkW / 2;
    pdf.text(linkText, w / 2, y, { align: "center" });
    pdf.link(linkX, y - 3, linkW, 5, { pageNumber: 1 });
  }

  pdf.setTextColor(...C.muted);
  if (pageNum !== undefined) {
    pdf.text(`${pageNum}${totalPages ? ` / ${totalPages}` : ""}`, w - 10, y, {
      align: "right",
    });
  }
}

// ---------------------------------------------------------------------------
// Section block
// ---------------------------------------------------------------------------

function renderSection(
  pdf: jsPDF,
  block: SectionBlock,
  ensureSpace: (n: number) => void,
  getY: () => number,
  setY: (y: number) => void,
) {
  if (!block.title) return;

  // Need space for title + underline + optional caption
  ensureSpace(20);
  let y = getY();

  // Section title (h1) — uppercase with orange underline
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(SECTION_FS);
  pdf.setTextColor(...C.dark);
  const titleText = block.title.toUpperCase();
  pdf.text(titleText, PM, y + 5);
  const titleW = pdf.getTextWidth(titleText);
  pdf.setDrawColor(...C.orange);
  pdf.setLineWidth(0.5);
  pdf.line(PM, y + 7.5, PM + titleW, y + 7.5);
  y += 12;

  // Caption — italic summary below section title
  if (block.caption) {
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(BODY_FS);
    pdf.setTextColor(...C.text);
    const captionLines = pdf.splitTextToSize(block.caption, P_CW);
    for (const line of captionLines) {
      if (y + BODY_LH > PM + P_CH) {
        pdf.addPage("a4", "portrait");
        y = PM;
      }
      pdf.text(line, PM, y);
      y += BODY_LH;
    }
    y += 3;
  }

  setY(y);
}

// ---------------------------------------------------------------------------
// Text block
// ---------------------------------------------------------------------------

function renderTextBlock(
  pdf: jsPDF,
  block: TextBlock,
  ensureSpace: (n: number) => void,
  getY: () => number,
  setY: (y: number) => void,
) {
  const isDyad = !!block.statusScore || !!block.dyadId;
  const rawText = block.text || "";
  const hasTitle = !!block.title;

  // Estimate space needed for title + first few lines
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(BODY_FS);
  const titleH = hasTitle ? 10 : 0;
  ensureSpace(titleH + 12);
  let y = getY();

  if (isDyad && block.title) {
    // Dyad-style card — header bar with accent stripe + status pill
    const palette = block.statusScore ? DYAD_COLORS[block.statusScore] : null;
    const barH = 8;
    pdf.setFillColor(...C.lightBg);
    pdf.rect(PM, y, P_CW, barH, "F");
    pdf.setFillColor(...C.accent);
    pdf.rect(PM, y, 0.8, barH, "F");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(TITLE_FS);
    pdf.setTextColor(...C.dark);
    pdf.text(block.title, PM + 3, y + 5);

    if (palette) {
      const pillText = palette.label;
      pdf.setFontSize(6.5);
      const pillW = pdf.getTextWidth(pillText) + 6;
      const pillX = PM + P_CW - pillW - 4;
      pdf.setFillColor(...palette.bg);
      pdf.roundedRect(pillX, y + 1.5, pillW, 5, 1, 1, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(6.5);
      pdf.setTextColor(...palette.text);
      pdf.text(pillText, pillX + 3, y + 4.8);
    }

    y += barH + 3;
  } else if (hasTitle) {
    // Sub-heading (h2/h3) — bold uppercase with thin gray rule below
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(TITLE_FS);
    pdf.setTextColor(...C.dark);
    const titleUp = block.title!.toUpperCase();
    pdf.text(titleUp, PM, y + 5);
    pdf.setDrawColor(180, 180, 180);
    pdf.setLineWidth(0.2);
    pdf.line(PM, y + 7, PM + P_CW, y + 7);
    y += 13;
  }

  // Split text into paragraphs, then render each
  const paragraphs = rawText.split(/\n\n+/);
  const bulletIndent = 4;
  const textLeft = PM;
  const textW = P_CW;
  const pageBottom = PM + P_CH;

  for (const para of paragraphs) {
    const trimmed = para.trim();
    if (!trimmed) continue;

    // Check if paragraph is a list of bullet items
    const lines = trimmed.split("\n");
    const isBulletBlock = lines.every(
      (l) => /^[-*+•]\s/.test(l.trim()) || !l.trim(),
    );

    if (isBulletBlock) {
      // Render each bullet with hanging indent
      for (const line of lines) {
        const cleaned = line.trim().replace(/^[-*+•]\s+/, "");
        if (!cleaned) continue;

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(BODY_FS);
        const bulletX = textLeft + 2;
        const contentX = bulletX + bulletIndent;
        const wrapW = textW - 2 - bulletIndent;

        // Parse bold spans within this bullet
        const runs = parseMarkdownRuns(cleaned);
        // For measurement, use stripped text
        const plain = stripMarkdown(cleaned);
        const wrapped = pdf.splitTextToSize(plain, wrapW);
        const neededH = wrapped.length * BODY_LH + 1;

        if (y + neededH > pageBottom) {
          pdf.addPage("a4", "portrait");
          y = PM;
        }

        // Bullet dot
        pdf.setFillColor(...C.text);
        pdf.circle(bulletX + 1, y - 1, 0.5, "F");

        // Render with bold preserved
        y = renderRichText(
          pdf,
          runs,
          contentX,
          wrapW,
          y,
          BODY_LH,
          BODY_FS,
          pageBottom,
        );
        y += 1; // spacing between bullets
      }
    } else {
      // Regular paragraph — render with bold preserved
      const runs = parseMarkdownRuns(trimmed);
      const plain = stripMarkdown(trimmed);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(BODY_FS);
      const wrapped = pdf.splitTextToSize(plain, textW);
      const neededH = wrapped.length * BODY_LH;

      if (y + Math.min(neededH, 20) > pageBottom) {
        pdf.addPage("a4", "portrait");
        y = PM;
      }

      y = renderRichText(
        pdf,
        runs,
        textLeft,
        textW,
        y,
        BODY_LH,
        BODY_FS,
        pageBottom,
      );
    }

    y += 2; // paragraph spacing
  }

  y += 2;
  setY(y);
}

// ---------------------------------------------------------------------------
// Table block
// ---------------------------------------------------------------------------

function renderTableBlock(
  pdf: jsPDF,
  block: TableBlock,
  ensureSpace: (n: number) => void,
  getY: () => number,
  setY: (y: number) => void,
) {
  const { columns, rows } = block.data;
  if (columns.length === 0) return;

  const colW = (P_CW - 4) / columns.length;
  const rowH = 6;
  const headerH = 7;
  const totalH = headerH + rows.length * rowH + 4 + (block.title ? 8 : 0);

  ensureSpace(Math.min(totalH, 50));
  let y = getY();

  // Title
  if (block.title) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(TITLE_FS);
    pdf.setTextColor(...C.dark);
    pdf.text(block.title, PM + 2, y + 5);
    y += 8;
  }

  // Header row
  pdf.setFillColor(...C.dark);
  pdf.rect(PM + 2, y, P_CW - 4, headerH, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7);
  pdf.setTextColor(...C.white);
  columns.forEach((col, i) => {
    pdf.text(col, PM + 2 + i * colW + 2, y + 4.5);
  });
  y += headerH;

  // Data rows
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  for (let r = 0; r < rows.length; r++) {
    if (y + rowH > PM + P_CH) {
      pdf.addPage("a4", "portrait");
      y = PM;
    }
    if (r % 2 === 0) {
      pdf.setFillColor(...C.lightBg);
      pdf.rect(PM + 2, y, P_CW - 4, rowH, "F");
    }
    pdf.setTextColor(...C.text);
    rows[r].forEach((cell, i) => {
      pdf.text(
        pdf.splitTextToSize(cell, colW - 4)[0] || "",
        PM + 2 + i * colW + 2,
        y + 4,
      );
    });
    y += rowH;
  }

  y += 4;
  setY(y);
}

// ---------------------------------------------------------------------------
// Dyad matrix — rendered on page 1 below the map
// ---------------------------------------------------------------------------

interface DyadCellRect {
  dyadId: string | undefined;
  x: number;
  y: number;
  w: number;
  h: number;
}

function renderDyadMatrixOnPage1(
  pdf: jsPDF,
  block: DyadMatrixBlock,
  mg: number,
  startY: number,
  lW: number,
  _lH: number,
): DyadCellRect[] {
  const dyads = block.dyads;
  const nDyads = dyads.length;
  if (nDyads === 0) return [];

  let y = startY;

  // Title
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7);
  pdf.setTextColor(...C.dark);
  pdf.text("DYAD RELATIONSHIPS", mg, y + 3);
  const tw = pdf.getTextWidth("DYAD RELATIONSHIPS");
  pdf.setDrawColor(...C.orange);
  pdf.setLineWidth(0.5);
  pdf.line(mg, y + 4.2, mg + tw, y + 4.2);
  y += 7;

  const rowLabelW = 16;
  const colW = Math.min((lW - mg * 2 - rowLabelW) / nDyads, 20);
  const tblX = mg;

  // Header row — country pairs
  const headerH = 10;
  pdf.setFillColor(...C.dark);
  pdf.rect(tblX, y, rowLabelW, headerH, "F");

  for (let c = 0; c < nDyads; c++) {
    const d = dyads[c];
    const cx = tblX + rowLabelW + c * colW;
    pdf.setFillColor(...C.dark);
    pdf.rect(cx, y, colW, headerH, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...C.white);

    let fs = 5.5;
    pdf.setFontSize(fs);
    while (pdf.getTextWidth(d.countryA) > colW - 2 && fs > 3.5) {
      fs -= 0.3;
      pdf.setFontSize(fs);
    }
    pdf.text(d.countryA, cx + colW / 2, y + 3.5, { align: "center" });
    pdf.text(d.countryB, cx + colW / 2, y + 7.5, { align: "center" });
  }
  y += headerH;

  // Status row
  const statusH = 6;
  pdf.setFillColor(...C.dark);
  pdf.rect(tblX, y, rowLabelW, statusH, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(5);
  pdf.setTextColor(...C.white);
  pdf.text("Status", tblX + rowLabelW / 2, y + statusH / 2 + 1, {
    align: "center",
  });

  for (let c = 0; c < nDyads; c++) {
    const d = dyads[c];
    const cx = tblX + rowLabelW + c * colW;
    const palette = DYAD_COLORS[d.statusScore];
    if (palette) {
      pdf.setFillColor(...palette.bg);
      pdf.rect(cx, y, colW, statusH, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(5.5);
      pdf.setTextColor(...palette.text);
      pdf.text(palette.label, cx + colW / 2, y + statusH / 2 + 1, {
        align: "center",
      });
    }
  }
  y += statusH;

  // Headline row — also collect cell rects for internal linking
  const headlineH = 12;
  const cellRects: DyadCellRect[] = [];
  pdf.setFillColor(...C.dark);
  pdf.rect(tblX, y, rowLabelW, headlineH, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(5);
  pdf.setTextColor(...C.white);
  pdf.text("Headline", tblX + rowLabelW / 2, y + headlineH / 2 + 1, {
    align: "center",
  });

  for (let c = 0; c < nDyads; c++) {
    const d = dyads[c];
    const cx = tblX + rowLabelW + c * colW;
    pdf.setFillColor(...C.lightBg);
    pdf.rect(cx, y, colW, headlineH, "F");
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(4.5);
    pdf.setTextColor(...C.text);
    const lines = pdf.splitTextToSize(d.headline || "", colW - 2);
    const startTy = y + 2.5;
    lines.slice(0, 4).forEach((line: string, i: number) => {
      pdf.text(line, cx + 1, startTy + i * 2.5);
    });
    // Capture the full column area (header + status + headline) for linking
    cellRects.push({
      dyadId: d.dyadId,
      x: cx,
      y: startY + 7,
      w: colW,
      h: headerH + statusH + headlineH,
    });
  }
  y += headlineH;

  // Grid lines
  pdf.setDrawColor(180, 180, 180);
  pdf.setLineWidth(0.1);
  for (let c = 0; c <= nDyads; c++) {
    const cx = tblX + rowLabelW + c * colW;
    pdf.line(cx, startY + 7, cx, y);
  }

  return cellRects;
}

// ---------------------------------------------------------------------------
// Key Events Gantt chart — native PDF
// ---------------------------------------------------------------------------

const CAT_COLORS: Record<string, [number, number, number]> = {
  election: [139, 58, 58],
  economic: [139, 125, 58],
  climate: [74, 122, 74],
  industry: [58, 107, 122],
  summit: [193, 122, 58],
};

function renderKeyEventsPdf(
  pdf: jsPDF,
  newPage: (orientation?: "portrait" | "landscape") => void,
) {
  newPage("portrait");
  const lW = pdf.internal.pageSize.getWidth();
  const lH = pdf.internal.pageSize.getHeight();
  const mg = 10;
  const labelW = 60; // mm for event name column (narrower in portrait)
  const timelineX = mg + labelW;
  const timelineW = lW - mg * 2 - labelW;
  const rowH = 4.5;
  const headerH = 6;

  const totalDays =
    (KEY_EVENTS_END.getTime() - KEY_EVENTS_START.getTime()) /
    (1000 * 60 * 60 * 24);

  function dayOffset(dateStr: string): number {
    return (
      (new Date(dateStr).getTime() - KEY_EVENTS_START.getTime()) /
      (1000 * 60 * 60 * 24)
    );
  }

  function pct(days: number): number {
    return days / totalDays;
  }

  let y = mg;

  // Title
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.setTextColor(...C.dark);
  pdf.text("Global Key Events", mg, y + 5);
  y += 8;

  // Subtitle
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(...C.muted);
  pdf.text("April 2026 \u2013 April 2027", mg, y + 3);
  y += 5;

  // Legend
  const legendItems = [
    { label: "Election", cat: "election" },
    { label: "Economic", cat: "economic" },
    { label: "Climate", cat: "climate" },
    { label: "Industry", cat: "industry" },
    { label: "Summit", cat: "summit" },
  ];
  let lx = mg;
  pdf.setFontSize(5.5);
  for (const item of legendItems) {
    const color = CAT_COLORS[item.cat];
    pdf.setFillColor(...color);
    pdf.rect(lx, y, 3, 3, "F");
    lx += 4;
    pdf.setTextColor(...C.muted);
    pdf.text(item.label, lx, y + 2.5);
    lx += pdf.getTextWidth(item.label) + 4;
  }

  // Confirmed / Expected / TBC legend
  lx += 4;
  pdf.setFillColor(...CAT_COLORS.summit);
  pdf.rect(lx, y, 6, 2.5, "F");
  lx += 7;
  pdf.setTextColor(...C.muted);
  pdf.text("Confirmed", lx, y + 2.5);
  lx += pdf.getTextWidth("Confirmed") + 3;

  pdf.setFillColor(...CAT_COLORS.summit);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gs = new (pdf.GState as any)({ opacity: 0.55 });
  pdf.saveGraphicsState();
  pdf.setGState(gs);
  pdf.rect(lx, y, 6, 2.5, "F");
  pdf.restoreGraphicsState();
  lx += 7;
  pdf.text("Expected", lx, y + 2.5);
  lx += pdf.getTextWidth("Expected") + 3;

  pdf.setDrawColor(...CAT_COLORS.summit);
  pdf.setLineWidth(0.3);
  const dashY = y + 1.25;
  for (let dx = 0; dx < 6; dx += 2) {
    pdf.line(lx + dx, dashY, lx + dx + 1, dashY);
  }
  lx += 7;
  pdf.text("Date TBC", lx, y + 2.5);

  y += 6;

  // Month headers
  const monthW = timelineW / KEY_EVENTS_MONTHS.length;
  pdf.setFillColor(245, 245, 245);
  pdf.rect(timelineX, y, timelineW, headerH, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(5);
  pdf.setTextColor(...C.muted);
  for (let i = 0; i < KEY_EVENTS_MONTHS.length; i++) {
    const mx = timelineX + i * monthW;
    pdf.text(KEY_EVENTS_MONTHS[i], mx + monthW / 2, y + headerH / 2 + 1, {
      align: "center",
    });
    if (i > 0) {
      pdf.setDrawColor(220, 220, 220);
      pdf.setLineWidth(0.1);
      pdf.line(mx, y, mx, y + headerH);
    }
  }
  y += headerH;

  // Sort events by start date
  const sorted = [...KEY_EVENTS].sort((a, b) => a.start.localeCompare(b.start));

  // Event rows
  for (const ev of sorted) {
    if (y + rowH > lH - 8) {
      newPage("portrait");
      y = mg;
    }

    // Alternating row background
    const rowIdx = sorted.indexOf(ev);
    if (rowIdx % 2 === 0) {
      pdf.setFillColor(250, 250, 250);
      pdf.rect(mg, y, lW - mg * 2, rowH, "F");
    }

    // Grid lines
    pdf.setDrawColor(240, 240, 240);
    pdf.setLineWidth(0.05);
    for (let i = 1; i < KEY_EVENTS_MONTHS.length; i++) {
      const gx = timelineX + i * monthW;
      pdf.line(gx, y, gx, y + rowH);
    }

    // Event label (right-aligned)
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(4.5);
    pdf.setTextColor(...C.text);
    const truncated = pdf.splitTextToSize(ev.name, labelW - 4)[0] || "";
    pdf.text(truncated, timelineX - 2, y + rowH / 2 + 1, { align: "right" });

    // Event bar
    const startDay = dayOffset(ev.start);
    const endDay = dayOffset(ev.end);
    const duration = Math.max(endDay - startDay, 1);
    const barX = timelineX + pct(startDay) * timelineW;
    const barW = Math.max(pct(duration) * timelineW, 1);
    const barY = y + (rowH - 2) / 2;
    const color = CAT_COLORS[ev.cat] || CAT_COLORS.summit;

    if (ev.status === "tbc") {
      pdf.setDrawColor(...color);
      pdf.setLineWidth(0.3);
      // Dashed rectangle
      const dashLen = 1;
      for (let dx = barX; dx < barX + barW; dx += dashLen * 2) {
        pdf.line(dx, barY, Math.min(dx + dashLen, barX + barW), barY);
        pdf.line(dx, barY + 2, Math.min(dx + dashLen, barX + barW), barY + 2);
      }
    } else {
      if (ev.status === "expected") {
        pdf.saveGraphicsState();
        pdf.setGState(gs);
      }
      pdf.setFillColor(...color);
      pdf.rect(barX, barY, barW, 2, "F");
      if (ev.status === "expected") {
        pdf.restoreGraphicsState();
      }
    }

    // Location text after bar
    if (ev.location) {
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(3.5);
      pdf.setTextColor(...C.muted);
      pdf.text(ev.location, barX + barW + 1, y + rowH / 2 + 0.8);
    }

    // Row bottom border
    pdf.setDrawColor(245, 245, 245);
    pdf.setLineWidth(0.05);
    pdf.line(mg, y + rowH, lW - mg, y + rowH);

    y += rowH;
  }
}
